import React, { useEffect, useRef, useState, useCallback } from 'react';
import OpenSeadragon from 'openseadragon';
import { useArtSync } from '../hooks/useArtSync';

// Small loading indicator for bottom left (manual clicks only)
function LoadingIndicator() {
  return (
    <div style={{
      position: 'fixed',
      bottom: '2rem',
      left: '2rem',
      zIndex: 100,
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.75rem 1.25rem',
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderRadius: '2rem',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    }}>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div style={{
        width: '1.25rem',
        height: '1.25rem',
        border: '2px solid rgba(156, 163, 175, 0.3)',
        borderTopColor: '#9ca3af',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      <span style={{
        fontSize: '0.875rem',
        color: '#9ca3af',
        fontWeight: 500,
      }}>
        Loading...
      </span>
    </div>
  );
}

// Full-screen loader (for initial load when no image is showing)
function LoadingHourglass() {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0, 0, 0, 0.95)',
      backdropFilter: 'blur(20px)',
      zIndex: 1000,
      color: '#9ca3af',
    }}>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div style={{
        width: '3rem',
        height: '3rem',
        border: '3px solid rgba(156, 163, 175, 0.2)',
        borderTopColor: '#9ca3af',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }} />
      <div style={{
        marginTop: '1.5rem',
        fontSize: '0.875rem',
        color: '#9ca3af',
      }}>
        Loading...
      </div>
    </div>
  );
}

export default function DisplayView() {
 const { currentUri, setArtwork, settings, setSettings } = useArtSync();

 // Dual viewer system for crossfade
 const viewerARef = useRef(null);
 const viewerBRef = useRef(null);
 const osdARef = useRef(null);
 const osdBRef = useRef(null);
 const [activeViewer, setActiveViewer] = useState('A'); // Which viewer is currently showing
 const [viewerAOpacity, setViewerAOpacity] = useState(1);
 const [viewerBOpacity, setViewerBOpacity] = useState(0);

 const kenBurnsTimerRef = useRef(null);
 const kenBurnsStopRef = useRef(false); // Signal to stop animation
 const [isLoading, setIsLoading] = useState(false);
 const [isLoadingNext, setIsLoadingNext] = useState(false); // Bottom-left indicator for manual clicks
 const loadingUriRef = useRef(null); // Prevent duplicate fetches
 const imageDimensionsRef = useRef(null); // Store actual image dimensions from API
 const [artworkData, setArtworkData] = useState(null);
 const [nextArtworkData, setNextArtworkData] = useState(null); // Preloaded next image
 const nextArtworkDataRef = useRef(null); // Mirror of nextArtworkData for access in effects
 const preloadingUriRef = useRef(null); // Track what we're preloading to avoid duplicates
 const preloadedViewerRef = useRef(null); // Track which viewer ('A' or 'B') has the preloaded content
 const isAutoAdvancingRef = useRef(false); // Track if current transition is auto-advance
 const apiUrl = process.env.REACT_APP_API_URL || 'https://artfolio-api.spb-f86.workers.dev';

 // Store settings and currentUri in refs to always get latest values
 const settingsRef = useRef(settings);
 const currentUriRef = useRef(currentUri);
 useEffect(() => {
   settingsRef.current = settings;
   currentUriRef.current = currentUri;
 }, [settings, currentUri]);

 // Keep nextArtworkData ref in sync with state
 useEffect(() => {
   nextArtworkDataRef.current = nextArtworkData;
 }, [nextArtworkData]);

 // Helper to create fresh OSD instance for a specific viewer element
 const createOSD = useCallback((viewerElement) => {
   if (!viewerElement) return null;

   return OpenSeadragon({
     element: viewerElement,
     prefixUrl: "//openseadragon.github.io/openseadragon/images/",
     visibilityRatio: 1.0,
     constrainDuringPan: false,
     showNavigationControl: false,
     maxZoomPixelRatio: 2,
     minZoomLevel: 0.5,
     maxZoomLevel: 5,
     crossOriginPolicy: 'Anonymous',
     immediateRender: true,
     animationTime: 0         // Disable OSD's built-in animation
   });
 }, []);

 // Function to preload next image INTO the inactive viewer
 // newActiveViewer param allows caller to specify which viewer is NOW active
 // (needed because React state updates are async)
 const preloadNextImage = useCallback((newActiveViewer) => {
   const queue = settingsRef.current.artworkQueue || [];
   const currentIndex = settingsRef.current.queueIndex || 0;
   const currentArtworkUri = currentUriRef.current;

   if (queue.length === 0) {
     console.log('Preload: No queue available');
     return;
   }

   // Calculate next URI
   let nextIndex = (currentIndex + 1) % queue.length;
   let nextUri = queue[nextIndex];

   // Skip current artwork if it's in the queue
   let attempts = 0;
   while (nextUri === currentArtworkUri && attempts < queue.length) {
     nextIndex = (nextIndex + 1) % queue.length;
     nextUri = queue[nextIndex];
     attempts++;
   }

   if (nextUri === currentArtworkUri) {
     console.log('Preload: All items in queue are current artwork');
     return;
   }

   // Skip if already preloading this URI
   if (preloadingUriRef.current === nextUri) {
     console.log('Preload: Already preloading', nextUri);
     return;
   }

   preloadingUriRef.current = nextUri;
   console.log('Preloading next image:', nextUri);

   // Use provided newActiveViewer or fall back to current state
   const currentActiveViewer = newActiveViewer || activeViewer;

   // Determine inactive viewer and track which one we're using
   const isViewerA = currentActiveViewer === 'A';
   const targetViewer = isViewerA ? 'B' : 'A'; // The viewer we're preloading INTO
   const inactiveViewerRef = isViewerA ? viewerBRef : viewerARef;
   const inactiveOsdRef = isViewerA ? osdBRef : osdARef;

   console.log('Preload: active viewer is', currentActiveViewer, ', loading into', targetViewer);

   // Destroy old OSD in inactive viewer
   if (inactiveOsdRef.current) {
     inactiveOsdRef.current.destroy();
     inactiveOsdRef.current = null;
   }

   fetch(`${apiUrl}?uri=${encodeURIComponent(nextUri)}`)
     .then(res => res.json())
     .then(data => {
       if (preloadingUriRef.current !== nextUri || !data || data.error) {
         return;
       }

       console.log('Preload data received:', data.title);
       nextArtworkDataRef.current = data;

       if (!data.image || !data.image.fullUrl) {
         console.log('Preload: No image URL');
         return;
       }

       // Create OSD in inactive viewer and load the image
       inactiveOsdRef.current = createOSD(inactiveViewerRef.current);
       if (!inactiveOsdRef.current) {
         console.log('Preload: Failed to create OSD');
         return;
       }

       const isIIIF = data.image.fullUrl.includes('info.json');
       let tileSource;
       if (isIIIF) {
         tileSource = data.image.fullUrl;
       } else {
         tileSource = { type: 'image', url: `${apiUrl}?proxy=${encodeURIComponent(data.image.fullUrl)}` };
       }

       console.log('Preload: Loading image into inactive viewer');

       inactiveOsdRef.current.addOnceHandler('open', () => {
         if (preloadingUriRef.current === nextUri) {
           console.log('Preload: Image loaded successfully into viewer', targetViewer);
           // Store that we have a fully loaded preload ready and which viewer it's in
           nextArtworkDataRef.current = { ...data, _preloadedInViewer: true };
           preloadedViewerRef.current = targetViewer;
         }
       });

       inactiveOsdRef.current.addOnceHandler('open-failed', () => {
         console.log('Preload: Image failed to load');
       });

       inactiveOsdRef.current.open(tileSource);
     })
     .catch(e => {
       console.error('Failed to preload next image:', e);
     });
 }, [apiUrl, activeViewer, createOSD]);

 // Load next artwork from queue when animation completes
 const loadNextArtwork = useCallback(() => {
   // Always get fresh values from refs
   const currentSettings = settingsRef.current;
   const currentArtworkUri = currentUriRef.current;
   const queue = currentSettings.artworkQueue || [];
   const currentIndex = currentSettings.queueIndex || 0;

   if (queue.length === 0) {
     console.log('No artwork queue available');
     return;
   }

   // Move to next in queue (wrap around), skipping current artwork if found
   let nextIndex = (currentIndex + 1) % queue.length;
   let nextUri = queue[nextIndex];

   // Skip current artwork if it's in the queue
   let attempts = 0;
   while (nextUri === currentArtworkUri && attempts < queue.length) {
     nextIndex = (nextIndex + 1) % queue.length;
     nextUri = queue[nextIndex];
     attempts++;
   }

   if (nextUri === currentArtworkUri) {
     console.log('All items in queue are current artwork, cannot advance');
     return;
   }

   console.log(`Auto-advancing to next artwork: ${nextIndex + 1}/${queue.length}`, nextUri);

   // Mark this as auto-advance so we can crossfade
   isAutoAdvancingRef.current = true;

   // Update queue index in Firebase and load artwork
   setSettings({ ...currentSettings, queueIndex: nextIndex });
   setArtwork(nextUri);
 }, [setSettings, setArtwork]);

 // Use settings.showDisplayDetails from Firebase (controlled by remote)
 const showDetailsPanel = settings.showDisplayDetails || false;
 const autoHideTimerRef = useRef(null);

 // Auto-hide details panel after 30 seconds
 useEffect(() => {
   if (showDetailsPanel) {
     // Clear any existing timer
     if (autoHideTimerRef.current) {
       clearTimeout(autoHideTimerRef.current);
     }

     // Set new timer to hide after 30 seconds
     autoHideTimerRef.current = setTimeout(() => {
       setSettings({ ...settings, showDisplayDetails: false });
     }, 30000);
   }

   // Cleanup on unmount or when panel closes
   return () => {
     if (autoHideTimerRef.current) {
       clearTimeout(autoHideTimerRef.current);
     }
   };
 }, [showDetailsPanel, settings, setSettings]);

 // Cleanup on unmount
 useEffect(() => {
   // Capture refs to avoid stale closure warnings
   const osdA = osdARef.current;
   const osdB = osdBRef.current;
   const timer = kenBurnsTimerRef.current;

   return () => {
     if (timer) cancelAnimationFrame(timer);
     if (osdA) osdA.destroy();
     if (osdB) osdB.destroy();
   };
 }, []);

 // Fetch artwork data when URI changes
 useEffect(() => {
   if (!currentUri) {
     setArtworkData(null);
     return;
   }

   const fetchDetails = async () => {
     try {
       const res = await fetch(`${apiUrl}?uri=${encodeURIComponent(currentUri)}`);
       const data = await res.json();
       if (data && !data.error) {
         setArtworkData(data);

         // If queue is empty, populate it with related works
         if (!settings.artworkQueue || settings.artworkQueue.length === 0) {
           if (data.artist?.name && data.artist.name !== 'Unknown') {
             console.log('Queue empty, fetching related works for:', data.artist.name);
             fetchRelatedWorks(data.artist.name);
           }
         }
       }
     } catch (e) {
       console.error("Failed to load artwork details", e);
       setArtworkData(null);
     }
   };

   fetchDetails();
 }, [currentUri, apiUrl]); // eslint-disable-line react-hooks/exhaustive-deps

 // Fetch related works to populate queue
 const fetchRelatedWorks = useCallback(async (artistName) => {
   try {
     let searchUrl = `${apiUrl}?search=${encodeURIComponent(artistName)}`;

     // Add filters from settings
     if (settings.artistFilter) searchUrl += `&artistFilter=${encodeURIComponent(settings.artistFilter)}`;
     if (settings.movementFilter) searchUrl += `&movementFilter=${encodeURIComponent(settings.movementFilter)}`;
     if (settings.mediumFilter) searchUrl += `&mediumFilter=${encodeURIComponent(settings.mediumFilter)}`;

     const res = await fetch(searchUrl);
     const data = await res.json();

     if (data.results && data.results.length > 0) {
       // Filter out current artwork from queue
       const filtered = data.results.filter(item => item.uri !== currentUri);
       const queue = filtered.map(item => item.uri);
       setSettings({
         ...settings,
         artworkQueue: queue,
         queueIndex: 0
       });
     }
   } catch (e) {
     console.error('Failed to fetch related works:', e);
   }
 }, [apiUrl, settings, setSettings, currentUri]);

 // Keyboard shortcut for details panel (i key)
 useEffect(() => {
   const handleKeyPress = (e) => {
     if (e.key === 'i' || e.key === 'I') {
       setSettings({ ...settings, showDisplayDetails: !settings.showDisplayDetails });
     }
   };

   window.addEventListener('keydown', handleKeyPress);
   return () => window.removeEventListener('keydown', handleKeyPress);
 }, [settings, setSettings]);

 // Ken Burns animation - accepts OSD instance and show callback
 const startKenBurns = useCallback((osdViewer, duration, onShowViewer) => {
   if (!osdViewer) {
     console.log('No viewer');
     return;
   }
   const viewer = osdViewer;

   const viewport = viewer.viewport;
   const tiledImage = viewer.world.getItemAt(0);
   if (!tiledImage || !viewport) {
     console.log('No tiledImage or viewport');
     return;
   }

   const containerSize = viewport.getContainerSize();

   // Get OSD's actual loaded image dimensions (most reliable)
   const osdSize = tiledImage.getContentSize();

   // Use OSD dimensions - API dimensions are often placeholders (e.g., 2000x2000 for Met)
   // Only use API dimensions if they're clearly not placeholders (not square, different from OSD)
   let imageWidth, imageHeight;
   const apiDims = imageDimensionsRef.current;
   const apiIsSquare = apiDims && apiDims.width === apiDims.height;
   const apiIsPlaceholder = apiIsSquare || !apiDims || !apiDims.width || !apiDims.height;

   if (!apiIsPlaceholder && Math.abs(apiDims.width / apiDims.height - osdSize.x / osdSize.y) < 0.1) {
     // API dimensions match OSD ratio - use API (higher precision)
     imageWidth = apiDims.width;
     imageHeight = apiDims.height;
     console.log('Using API dimensions:', imageWidth, 'x', imageHeight);
   } else {
     // Use OSD dimensions (actual loaded image)
     imageWidth = osdSize.x;
     imageHeight = osdSize.y;
     console.log('Using OSD dimensions:', imageWidth, 'x', imageHeight, apiIsPlaceholder ? '(API was placeholder)' : '(API ratio mismatch)');
   }

   const containerAspect = containerSize.x / containerSize.y;
   const imageAspect = imageWidth / imageHeight;

   console.log('Ken Burns:', {
     container: `${containerSize.x}x${containerSize.y} (${containerAspect.toFixed(2)})`,
     image: `${imageWidth}x${imageHeight} (${imageAspect.toFixed(2)})`,
     duration
   });

   // Normalize image dimensions for OSD viewport coordinates (width = 1)
   const normalizedHeight = 1 / imageAspect;

   let fillZoom;
   let panDirection;

   // Simple logic: compare image aspect to container aspect
   // - If image wider than container: fill height, pan horizontally
   // - If image taller than container: fill width, pan vertically
   // - If same aspect: no pan needed

   if (Math.abs(imageAspect - containerAspect) < 0.05) {
     // Aspects nearly match - just show the image
     console.log('Aspects match - showing without pan');
     viewport.goHome(true);
     if (onShowViewer) onShowViewer();
     // Note: Auto-advance disabled - stays on selected artwork
     return;
   }

   if (imageAspect > containerAspect) {
     // Image is wider than container - fill height, pan horizontally
     fillZoom = imageAspect / containerAspect;
     panDirection = 'horizontal';
   } else {
     // Image is taller than container - fill width, pan vertically
     fillZoom = 1;
     panDirection = 'vertical';
   }

   // Cap zoom to prevent excessive zoom on extreme aspect ratios
   const maxZoom = 2.5;
   if (fillZoom > maxZoom) {
     console.log(`Capping fillZoom from ${fillZoom.toFixed(2)} to ${maxZoom}`);
     fillZoom = maxZoom;
   }

   // Calculate visible area at this zoom level
   const visibleWidth = 1 / fillZoom;
   const visibleHeight = visibleWidth / containerAspect;

   console.log('Calculated:', { fillZoom: fillZoom.toFixed(2), panDirection, visibleWidth: visibleWidth.toFixed(3), visibleHeight: visibleHeight.toFixed(3) });

   let startPoint, endPoint;

   if (panDirection === 'horizontal') {
     // Pan from left to right
     const startX = visibleWidth / 2;
     const endX = 1 - visibleWidth / 2;
     const centerY = normalizedHeight / 2;

     // Make sure we don't go out of bounds
     const safeStartX = Math.max(visibleWidth / 2, startX);
     const safeEndX = Math.min(1 - visibleWidth / 2, endX);

     startPoint = new OpenSeadragon.Point(safeStartX, centerY);
     endPoint = new OpenSeadragon.Point(safeEndX, centerY);
   } else {
     // Pan from top to bottom
     const centerX = 0.5;
     const startY = visibleHeight / 2;
     const endY = normalizedHeight - visibleHeight / 2;

     console.log('Vertical pan:', { normalizedHeight: normalizedHeight.toFixed(3), visibleHeight: visibleHeight.toFixed(3), startY: startY.toFixed(3), endY: endY.toFixed(3) });

     // Clamp to ensure we don't pan beyond image bounds
     const safeStartY = Math.max(visibleHeight / 2, Math.min(normalizedHeight - visibleHeight / 2, startY));
     const safeEndY = Math.max(visibleHeight / 2, Math.min(normalizedHeight - visibleHeight / 2, endY));

     startPoint = new OpenSeadragon.Point(centerX, safeStartY);
     endPoint = new OpenSeadragon.Point(centerX, safeEndY);
   }

   const panDistance = Math.sqrt(
     Math.pow(endPoint.x - startPoint.x, 2) + Math.pow(endPoint.y - startPoint.y, 2)
   );
   console.log('Pan:', {
     start: `(${startPoint.x.toFixed(3)}, ${startPoint.y.toFixed(3)})`,
     end: `(${endPoint.x.toFixed(3)}, ${endPoint.y.toFixed(3)})`,
     distance: panDistance.toFixed(3)
   });

   // Small delay to let OSD initialize, then apply our settings
   requestAnimationFrame(() => {
     // Robust validation before starting
     if (!viewer) {
       console.log('Ken Burns: viewer is null');
       return;
     }
     const vp = viewer.viewport;
     if (!vp) {
       console.log('Ken Burns: viewport is null');
       return;
     }

     try {
       vp.zoomTo(fillZoom, null, true);
       vp.panTo(startPoint, true);
       viewer.forceRedraw(); // Ensure the view updates
     } catch (e) {
       console.log('Ken Burns: failed to set initial position', e.message);
       return;
     }

     // Show after positioning succeeded
     if (onShowViewer) onShowViewer();

     const durationMs = duration * 1000;
     const startTime = performance.now();

     const animate = (currentTime) => {
       // Check stop flag first
       if (kenBurnsStopRef.current) {
         console.log('Ken Burns: stop requested');
         return;
       }
       // Robust check: viewer must exist and not be destroyed
       if (!viewer || !viewer.viewport || typeof viewer.forceRedraw !== 'function') {
         console.log('Ken Burns: viewer destroyed, stopping animation');
         return;
       }

       const vp = viewer.viewport;

       const elapsed = currentTime - startTime;
       const progress = Math.min(elapsed / durationMs, 1);

       const currentX = startPoint.x + (endPoint.x - startPoint.x) * progress;
       const currentY = startPoint.y + (endPoint.y - startPoint.y) * progress;

       try {
         vp.panTo(new OpenSeadragon.Point(currentX, currentY), true);
         viewer.forceRedraw();
       } catch (e) {
         console.log('Ken Burns: animation error, stopping', e.message);
         return;
       }

       if (progress < 1) {
         kenBurnsTimerRef.current = requestAnimationFrame(animate);
       } else {
         console.log('Ken Burns animation complete - loading next artwork');
         loadNextArtwork();
       }
     };

     console.log(`Starting Ken Burns animation (${duration}s)`);
     kenBurnsStopRef.current = false; // Reset stop flag
     kenBurnsTimerRef.current = requestAnimationFrame(animate);
   });
 }, [loadNextArtwork]);

 // Load Image - uses dual viewer system for crossfade
 useEffect(() => {
   // Determine which viewer refs to use based on activeViewer state
   const isViewerA = activeViewer === 'A';
   const activeViewerRef = isViewerA ? viewerARef : viewerBRef;
   const activeOsdRef = isViewerA ? osdARef : osdBRef;
   const inactiveViewerRef = isViewerA ? viewerBRef : viewerARef;
   const inactiveOsdRef = isViewerA ? osdBRef : osdARef;

   if (!currentUri || !activeViewerRef.current) return;

   // Prevent duplicate fetches for same URI
   if (loadingUriRef.current === currentUri) {
     console.log('Already loading this URI, skipping');
     return;
   }
   loadingUriRef.current = currentUri;

   // Stop any running Ken Burns animation
   kenBurnsStopRef.current = true;
   if (kenBurnsTimerRef.current) cancelAnimationFrame(kenBurnsTimerRef.current);

   // Determine if this is auto-advance or manual click
   const isAutoAdvance = isAutoAdvancingRef.current;
   isAutoAdvancingRef.current = false; // Reset for next transition

   // Clear preloaded data tracking (we'll use the data if available, then clear)
   // This allows preloadNextImage to start fresh for the next-next image
   preloadingUriRef.current = null;

   if (!isAutoAdvance) {
     console.log('Manual click detected - clearing preload');
     nextArtworkDataRef.current = null;
     preloadedViewerRef.current = null;
     setNextArtworkData(null);
   }

   // For manual clicks: show bottom-left loader
   if (!isAutoAdvance) {
     console.log('Manual click: showing bottom-left loader');
     setIsLoadingNext(true);
   } else {
     console.log('Auto-advance: preparing crossfade');
   }

   // Show full-screen loader only on first load (no image visible yet)
   const isFirstLoad = !osdARef.current && !osdBRef.current;
   if (isFirstLoad) {
     setIsLoading(true);
   }

   // Check if we have a fully preloaded image - use tracked viewer ref
   const preloadedData = nextArtworkDataRef.current;
   const preloadedViewer = preloadedViewerRef.current; // 'A' or 'B' or null
   const preloadedOsdRef = preloadedViewer === 'A' ? osdARef : preloadedViewer === 'B' ? osdBRef : null;

   // Debug logging for preload matching
   if (isAutoAdvance && preloadedData) {
     console.log('Preload check:', {
       preloadedId: preloadedData.id,
       currentUri: currentUri,
       idsMatch: preloadedData.id === currentUri,
       hasViewerFlag: preloadedData._preloadedInViewer,
       preloadedViewer: preloadedViewer,
       osdExists: preloadedOsdRef?.current ? 'yes' : 'no'
     });
   }

   const hasPreloadedViewer = isAutoAdvance && preloadedData &&
     preloadedData.id === currentUri && preloadedData._preloadedInViewer &&
     preloadedOsdRef && preloadedOsdRef.current;

   const duration = settings.animationDuration || 600;

   if (hasPreloadedViewer) {
     // Image is already loaded in the tracked preloaded viewer - just start Ken Burns and crossfade!
     console.log('Using preloaded viewer', preloadedViewer, 'for:', currentUri);
     setIsLoading(false);
     setIsLoadingNext(false);

     // Store dimensions from preloaded data
     imageDimensionsRef.current = {
       width: preloadedData.image.fullWidth || preloadedData.image.width,
       height: preloadedData.image.fullHeight || preloadedData.image.height
     };

     // Capture the OSD reference now (before any async operations)
     const preloadedOsd = preloadedOsdRef.current;

     // Clear preloaded viewer tracking since we're using it now
     preloadedViewerRef.current = null;

     // The preloaded viewer has the OSD loaded, just start Ken Burns
     const showViewer = () => {
       console.log('Starting 3-second crossfade to viewer', preloadedViewer);
       if (preloadedViewer === 'A') {
         setViewerAOpacity(1);
         setViewerBOpacity(0);
       } else {
         setViewerBOpacity(1);
         setViewerAOpacity(0);
       }
       setTimeout(() => {
         setActiveViewer(preloadedViewer);
         // Pass new active viewer since React state hasn't updated yet
         preloadNextImage(preloadedViewer);
       }, 3000);
     };

     requestAnimationFrame(() => {
       if (preloadedOsd) {
         startKenBurns(preloadedOsd, duration, showViewer);
       }
     });

     loadingUriRef.current = null;
     return; // Skip the normal loading flow
   }

   // Normal loading flow - no preloaded viewer available
   // First load goes to active viewer, subsequent loads go to inactive for crossfade
   const targetViewerRef = isFirstLoad ? activeViewerRef : inactiveViewerRef;
   const targetOsdRef = isFirstLoad ? activeOsdRef : inactiveOsdRef;
   const targetViewerName = isFirstLoad ? (isViewerA ? 'A' : 'B') : (isViewerA ? 'B' : 'A');

   // If we're about to destroy the preload's viewer, clear preload tracking
   if (preloadedViewerRef.current === targetViewerName) {
     console.log('Clearing preload - destroying preload viewer');
     preloadedViewerRef.current = null;
     nextArtworkDataRef.current = null;
   }

   // Destroy existing OSD in target viewer
   if (targetOsdRef.current) {
     console.log('Destroying OSD in viewer', targetViewerName);
     targetOsdRef.current.destroy();
     targetOsdRef.current = null;
   }

   // Create fresh OSD instance
   targetOsdRef.current = createOSD(targetViewerRef.current);
   if (!targetOsdRef.current) {
     console.log('Failed to create OSD instance');
     setIsLoading(false);
     setIsLoadingNext(false);
     return;
   }

   // Check if we have preloaded DATA (but not viewer) for this URI
   const hasPreloadedData = isAutoAdvance && preloadedData && preloadedData.id === currentUri;

   if (hasPreloadedData) {
     console.log('Using preloaded data for:', currentUri);
   } else {
     console.log('Fetching artwork:', currentUri);
   }

   // Use preloaded data or fetch
   const dataPromise = hasPreloadedData
     ? Promise.resolve(preloadedData)
     : fetch(`${apiUrl}?uri=${encodeURIComponent(currentUri)}`).then(res => res.json());

   dataPromise
     .then(data => {
       console.log('Artwork data:', data);

       if (!data || !data.image || !data.image.fullUrl) {
         console.log('No image URL available');
         setIsLoading(false);
         setIsLoadingNext(false);
         loadingUriRef.current = null;
         return;
       }

       // Store actual image dimensions
       imageDimensionsRef.current = {
         width: data.image.fullWidth || data.image.width,
         height: data.image.fullHeight || data.image.height
       };

       const isIIIF = data.image.fullUrl.includes('info.json');
       let tileSource;

       if (isIIIF) {
         tileSource = data.image.fullUrl;
         console.log('Loading IIIF image:', tileSource);
       } else {
         const proxyUrl = `${apiUrl}?proxy=${encodeURIComponent(data.image.fullUrl)}`;
         tileSource = { type: 'image', url: proxyUrl };
         console.log('Loading static image via proxy:', proxyUrl);
       }

       const loadingUri = currentUri;
       let loadHandled = false;

       const loadTimeout = setTimeout(() => {
         if (!loadHandled && loadingUriRef.current === loadingUri) {
           console.log('Load timeout');
           loadHandled = true;
           setIsLoading(false);
           setIsLoadingNext(false);
           loadingUriRef.current = null;
         }
       }, 10000);

       const openHandler = () => {
         console.log('Image opened for:', loadingUri);

         if (loadingUriRef.current !== loadingUri) {
           console.log('Stale handler, ignoring');
           return;
         }

         loadingUriRef.current = null;

         const tiledImage = targetOsdRef.current?.world.getItemAt(0);
         if (!tiledImage) {
           console.log('No tiledImage, using fallback');
           setTimeout(() => {
             if (!loadHandled) {
               loadHandled = true;
               clearTimeout(loadTimeout);
               finishLoad();
             }
           }, 500);
           return;
         }

         const fullyLoaded = tiledImage.getFullyLoaded();

         const finishLoad = () => {
           if (loadHandled) return;
           loadHandled = true;
           clearTimeout(loadTimeout);
           setIsLoading(false);
           setIsLoadingNext(false);

           // Define callback to show the viewer
           const showViewer = () => {
             if (isFirstLoad) {
               // First load: just show active viewer (A) immediately
               console.log('First load - showing viewer A');
               setViewerAOpacity(1);
               // Safe to preload immediately - activeViewer is 'A', preload targets 'B'
               preloadNextImage('A');
             } else {
               // Crossfade: fade in inactive, fade out active
               // Auto-advance: 3 second transition (set via CSS)
               // Manual click: also crossfade but viewer will use same 3s CSS transition
               const transitionName = isAutoAdvance ? '3-second' : 'quick';
               console.log(`Starting ${transitionName} crossfade`);

               if (isViewerA) {
                 // A is active (visible), B is inactive (loading) -> fade to B
                 setViewerBOpacity(1);
                 setViewerAOpacity(0);
               } else {
                 // B is active, A is inactive -> fade to A
                 setViewerAOpacity(1);
                 setViewerBOpacity(0);
               }
               // After crossfade completes, swap active viewer and start preload
               const newActiveViewer = isViewerA ? 'B' : 'A';
               setTimeout(() => {
                 setActiveViewer(newActiveViewer);
                 // Pass new active viewer since React state hasn't updated yet
                 preloadNextImage(newActiveViewer);
               }, 3000);
             }
           };

           // Start Ken Burns on the target viewer
           requestAnimationFrame(() => {
             startKenBurns(targetOsdRef.current, duration, showViewer);
           });
         };

         if (fullyLoaded) {
           finishLoad();
         } else {
           const loadedHandler = (event) => {
             if (event.fullyLoaded) {
               tiledImage.removeHandler('fully-loaded-change', loadedHandler);
               finishLoad();
             }
           };
           tiledImage.addHandler('fully-loaded-change', loadedHandler);

           setTimeout(() => {
             if (!loadHandled) {
               tiledImage.removeHandler('fully-loaded-change', loadedHandler);
               finishLoad();
             }
           }, 3000);
         }
       };

       const failHandler = (event) => {
         console.error('Failed to open image:', event);
         if (!loadHandled && loadingUriRef.current === loadingUri) {
           loadHandled = true;
           clearTimeout(loadTimeout);
           setIsLoading(false);
           setIsLoadingNext(false);
           loadingUriRef.current = null;
         }
       };

       targetOsdRef.current.removeAllHandlers('open');
       targetOsdRef.current.removeAllHandlers('open-failed');
       targetOsdRef.current.addOnceHandler('open', openHandler);
       targetOsdRef.current.addOnceHandler('open-failed', failHandler);

       targetOsdRef.current.open(tileSource);
     })
     .catch(err => {
       console.error("Failed to fetch artwork details:", err);
       setIsLoading(false);
       setIsLoadingNext(false);
       loadingUriRef.current = null; // Allow retry
     });
 }, [currentUri]); // eslint-disable-line react-hooks/exhaustive-deps

 return (
   <>
     <style>{`body { background-color: #000 !important; }`}</style>
     {/* Dual viewer system for crossfade effect */}
     <div
       ref={viewerARef}
       style={{
         position: 'absolute',
         top: 0,
         left: 0,
         width: '100vw',
         height: '100vh',
         backgroundColor: '#000',
         opacity: viewerAOpacity,
         transition: 'opacity 3s ease-in-out',
         cursor: 'none',
         zIndex: activeViewer === 'A' ? 2 : 1
       }}
     />
     <div
       ref={viewerBRef}
       style={{
         position: 'absolute',
         top: 0,
         left: 0,
         width: '100vw',
         height: '100vh',
         backgroundColor: '#000',
         opacity: viewerBOpacity,
         transition: 'opacity 3s ease-in-out',
         cursor: 'none',
         zIndex: activeViewer === 'B' ? 2 : 1
       }}
     />
     {isLoading && <LoadingHourglass />}
     {isLoadingNext && <LoadingIndicator />}

     {/* Details Lower Third Panel - Press 'i' to toggle */}
     {showDetailsPanel && artworkData && (
       <div
         style={{
           position: 'fixed',
           inset: 0,
           zIndex: 200,
           pointerEvents: 'auto',
           cursor: 'pointer'
         }}
         onClick={() => setSettings({ ...settings, showDisplayDetails: false })}
       >
         <div
           style={{
             position: 'fixed',
             bottom: 0,
             left: 0,
             right: 0,
             maxHeight: '50vh',
             overflowY: 'auto',
             backgroundColor: 'rgba(0, 0, 0, 0.7)',
             backdropFilter: 'blur(40px) saturate(180%)',
             WebkitBackdropFilter: 'blur(40px) saturate(180%)',
             borderTop: '1px solid rgba(255, 255, 255, 0.15)',
             padding: '2rem',
             cursor: 'auto',
             animation: 'slideUp 0.3s ease-out'
           }}
           onClick={(e) => e.stopPropagation()}
         >
           <button
             style={{
               position: 'absolute',
               top: '1rem',
               right: '1rem',
               background: 'rgba(0, 0, 0, 0.5)',
               border: '1px solid rgba(255, 255, 255, 0.2)',
               borderRadius: '50%',
               width: '2rem',
               height: '2rem',
               color: '#f3f4f6',
               fontSize: '1.25rem',
               cursor: 'pointer',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               transition: 'all 0.2s'
             }}
             onClick={() => setSettings({ ...settings, showDisplayDetails: false })}
             onMouseEnter={(e) => {
               e.target.style.background = 'rgba(0, 0, 0, 0.8)';
               e.target.style.borderColor = 'rgba(255, 255, 255, 0.4)';
             }}
             onMouseLeave={(e) => {
               e.target.style.background = 'rgba(0, 0, 0, 0.5)';
               e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
             }}
           >
             ✕
           </button>

           <div style={{ maxWidth: '60rem', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
             <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '1rem' }}>
               <h2 style={{ fontSize: '2rem', fontWeight: 700, color: 'white', margin: '0 0 0.5rem 0', lineHeight: 1.2 }}>
                 {artworkData.title}
               </h2>
               {artworkData.artist?.name && (
                 <p style={{ fontSize: '1.25rem', color: '#93c5fd', fontWeight: 500, margin: '0.25rem 0' }}>
                   {artworkData.artist.name}
                 </p>
               )}
               {(artworkData.artist?.birthYear || artworkData.artist?.deathYear) && (
                 <p style={{ fontSize: '1rem', color: '#9ca3af', margin: '0.25rem 0 0 0' }}>
                   {artworkData.artist.birthYear || '?'} – {artworkData.artist.deathYear || 'present'}
                 </p>
               )}
             </div>

             {artworkData.details && (
               <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                 {artworkData.details.description && (
                   <p style={{ fontSize: '0.95rem', color: '#d1d5db', lineHeight: 1.6, margin: 0 }}>
                     {artworkData.details.description}
                   </p>
                 )}

                 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0.75rem' }}>
                   {artworkData.details.dimensions && (
                     <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.875rem' }}>
                       <span style={{ color: '#9ca3af', fontWeight: 500, minWidth: '6rem' }}>Dimensions:</span>
                       <span style={{ color: '#e5e7eb' }}>{artworkData.details.dimensions}</span>
                     </div>
                   )}
                   {artworkData.details.labelLine && (
                     <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.875rem' }}>
                       <span style={{ color: '#9ca3af', fontWeight: 500, minWidth: '6rem' }}>Medium:</span>
                       <span style={{ color: '#e5e7eb' }}>{artworkData.details.labelLine}</span>
                     </div>
                   )}
                   {artworkData.details.source && (
                     <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.875rem' }}>
                       <span style={{ color: '#9ca3af', fontWeight: 500, minWidth: '6rem' }}>Source:</span>
                       <span style={{ color: '#3b82f6', fontWeight: 600 }}>{artworkData.details.source.toUpperCase()}</span>
                     </div>
                   )}
                 </div>
               </div>
             )}

             <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.5rem 0 0 0', textAlign: 'center' }}>
               Press 'i' to close
             </p>
           </div>
         </div>
       </div>
     )}
   </>
 );
}
